import {action, computed, observable, makeObservable} from 'mobx'
import {arrayCompareObjects, deepClone} from '@oc/utils'
import {type ValidationResult, ValidationState} from '../Validation'
import {OcGenericDialogOutput, OcGenericDialogStatus} from './OcGenericDialogInterfacesExport'
import {messages} from '../translations'

export interface OcGenericDialogSingleFieldStateInput<SourceType, FieldType = unknown> {
    groupName: string
    fieldName: string
    mandatory: boolean
    mandatoryErrorText?: string
    customValidationResult?: any
    defaultValue?: FieldType
    initialValue?: FieldType
    setValueFunction?: (
        data: SourceType,
        value: FieldType,
        dialogOutput: OcGenericDialogOutput[],
        status?: OcGenericDialogStatus
    ) => void
    validateFunction?: (value: FieldType, dialogOutput: OcGenericDialogOutput[]) => ValidationResult
    displayCondition?: (dialogOutput: OcGenericDialogOutput[]) => boolean
}

const mandatoryFieldErrorValidationResult = {
    error: ValidationState.Error,
    message: messages.getText('genericDialog', 'mandatoryDefaultMessage'),
    sanitizedValue: undefined
}

export class OcGenericDialogSingleFieldState<SourceType> {
    @observable private _value?: any = undefined
    @observable private _isChanged = false
    @observable private _validationState = ValidationState.OK
    @observable private _errorMessage?: string = undefined
    @observable private _customValidationResult?: any = undefined
    @observable private _setValueFunction: (
        data: SourceType,
        value: any,
        dialogOutput: OcGenericDialogOutput[],
        status?: OcGenericDialogStatus
    ) => void
    @observable private _validateFunction?: (value: any, dialogOutput: OcGenericDialogOutput[]) => ValidationResult
    @observable public readonly displayCondition?: (dialogOutput: OcGenericDialogOutput[]) => boolean
    @observable public readonly groupName: string
    @observable public readonly fieldName: string
    @observable public readonly mandatory: boolean
    @observable public readonly defaultValue: any
    @observable public readonly initialValue: any
    @observable public readonly mandatoryErrorText?: string = undefined

    @computed
    public get value() {
        return this._value
    }

    @computed
    public get isChanged() {
        return this._isChanged
    }

    @computed
    public get setValueFunction() {
        return this._setValueFunction
    }

    @computed
    public get validationState() {
        return this._validationState
    }

    @computed
    public get errorMessage() {
        return this._errorMessage
    }

    @computed
    public get customValidationResult() {
        return this._customValidationResult
    }

    @computed
    public get validateFunction() {
        return this._validateFunction
    }

    constructor(inputProps: OcGenericDialogSingleFieldStateInput<SourceType>) {
        makeObservable(this)
        this.groupName = inputProps.groupName
        this.fieldName = inputProps.fieldName
        this.mandatory = inputProps.mandatory
        this.defaultValue = inputProps.defaultValue
        this.initialValue = inputProps.initialValue
        this.displayCondition = inputProps.displayCondition
        this.mandatoryErrorText = inputProps.mandatoryErrorText

        this._setValueFunction = inputProps.setValueFunction
            ? inputProps.setValueFunction
            : (
                  data: SourceType,
                  value: any,
                  _dialogOutput: OcGenericDialogOutput[],
                  _status?: OcGenericDialogStatus
              ) => {
                  if (data) {
                      data[this.fieldName as keyof SourceType] = deepClone(value)
                  }
              }

        if (inputProps.validateFunction) {
            this._validateFunction = inputProps.validateFunction
            this.validateValue = (value: any, dialogOutput: OcGenericDialogOutput[]) => {
                const resultBasic = this.validateBasic(value, dialogOutput)
                if (resultBasic.error === ValidationState.OK && this._validateFunction) {
                    return this._validateFunction(value, dialogOutput)
                }
                return resultBasic
            }
        }
    }

    @action.bound
    public setValue(value: any, dialogOutput: OcGenericDialogOutput[]): void {
        const result = this.validateValue(value, dialogOutput)
        this.setValidationResult(result)
        this._value = value
        this._isChanged = true
    }

    @action.bound
    public resetValue(): void {
        this._value = undefined
        this._isChanged = false
        this._validationState = ValidationState.OK
        this._errorMessage = undefined
        this._customValidationResult = undefined
    }

    private validateBasic(value: any, dialogOutput: OcGenericDialogOutput[]): ValidationResult {
        if (
            this.mandatory &&
            (typeof value === 'undefined' || value === '') &&
            (!this.displayCondition || this.displayCondition(dialogOutput))
        ) {
            const result = {...mandatoryFieldErrorValidationResult}
            if (this.mandatoryErrorText) {
                result.message = this.mandatoryErrorText
            }
            return result
        }
        return {
            error: ValidationState.OK,
            message: '',
            sanitizedValue: value
        }
    }

    public validateValue(value: any, dialogOutput: OcGenericDialogOutput[]): ValidationResult {
        return this.validateBasic(value, dialogOutput)
    }

    @action.bound
    public setValidationResult(validationResult: ValidationResult) {
        this._validationState = validationResult.error
        this._customValidationResult = validationResult.customValidationResult
        if (validationResult.message === '') {
            this._errorMessage = undefined
        } else {
            this._errorMessage = validationResult.message
        }
    }
}

export class OcGenericDialogState<SourceType> {
    @observable private _data: SourceType[]
    @observable private _allowedGroups: string[] = []
    @observable private _changedFields: OcGenericDialogSingleFieldState<SourceType>[] = []
    @observable private _status?: OcGenericDialogStatus = undefined
    @observable private _createMode = false
    @observable private _bulkEdit = false
    @observable private _statusChanged = false

    @computed
    public get isChanged(): boolean {
        return !!this._changedFields.find(field => field.isChanged) || this._statusChanged
    }

    @computed
    public get hasError(): boolean {
        return !!this._changedFields.find(field => field.validationState === ValidationState.Error)
    }

    @computed
    public get data(): SourceType[] {
        return this._data
    }

    @computed
    public get changedFields(): OcGenericDialogSingleFieldState<SourceType>[] {
        return this._changedFields
    }

    @computed
    public get allowedGroups(): string[] {
        return this._allowedGroups
    }

    @computed
    public get status(): OcGenericDialogStatus | undefined {
        return this._status
    }

    @computed
    public get dialogOutput(): OcGenericDialogOutput[] {
        return this._changedFields.map(item => {
            const valueToReturn = item.isChanged ? item.value : this._createMode ? item.defaultValue : item.initialValue
            return {
                groupName: item.groupName,
                fieldName: item.fieldName,
                value: valueToReturn,
                isChanged: item.isChanged,
                isDisabled: false
            }
        })
    }

    @computed
    public get createMode(): boolean {
        return this._createMode
    }

    @computed
    public get bulkEdit(): boolean {
        return this._bulkEdit
    }

    constructor(
        fields: OcGenericDialogSingleFieldStateInput<SourceType>[],
        initialStatus?: OcGenericDialogStatus,
        ...data: SourceType[]
    ) {
        makeObservable(this)
        this._data = data
        this.applyChanges = this.applyChanges.bind(this)
        this._status = initialStatus

        fields.forEach(fieldProps => {
            this._changedFields.push(new OcGenericDialogSingleFieldState(fieldProps))
        })
    }

    public applyChanges(): void {
        if (this._data.length > 0 && !this._createMode) {
            let toUpdate = [this.data[0]]
            if (this._bulkEdit) {
                toUpdate = this.data
            }
            toUpdate.forEach(dataItem => {
                this._changedFields
                    .filter(field => field.isChanged)
                    .forEach(field => field.setValueFunction(dataItem, field.value, this.dialogOutput, this._status))
            })
        }
    }

    @action.bound
    public resetState(
        fields: OcGenericDialogSingleFieldStateInput<SourceType>[],
        initialStatus?: OcGenericDialogStatus,
        ...data: SourceType[]
    ): void {
        this._status = initialStatus
        this._statusChanged = false
        this._bulkEdit = data.length > 1 ? true : false
        this._createMode = data.length === 0 ? true : false
        if (fields) {
            this.setUpState(fields, ...data)
            this.setAllowedGroups()
        } else {
            this._changedFields = []
            this._allowedGroups = []
        }
    }

    @action.bound
    private setUpState(fields: OcGenericDialogSingleFieldStateInput<SourceType>[], ...data: SourceType[]): void {
        this._data = data
        this._changedFields = []
        fields.forEach(fieldProps => {
            this._changedFields.push(new OcGenericDialogSingleFieldState(fieldProps))
        })
    }

    @action.bound
    private setAllowedGroups(): void {
        const newGroups = new Set<string>()
        this._changedFields.forEach(field => {
            if (!field.displayCondition) {
                newGroups.add(field.groupName)
            } else if (field.displayCondition(this.dialogOutput)) {
                newGroups.add(field.groupName)
            }
        })
        const newGroupArray = Array.from(newGroups)
        if (!arrayCompareObjects(newGroupArray, this._allowedGroups, (item1, item2) => item1 === item2)) {
            this._allowedGroups = newGroupArray
        }
    }

    @action.bound
    public resetField(group: string, field: string): void {
        this._changedFields.find(item => item.groupName === group && item.fieldName === field)?.resetValue()
    }

    @action.bound
    public onChange(group: string, field: string, value: any): void {
        this._changedFields
            .find(item => item.groupName === group && item.fieldName === field)
            ?.setValue(value, this.dialogOutput)
        this.setAllowedGroups()
    }

    @action.bound
    public setStatus(newStatus: OcGenericDialogStatus): void {
        this._status = newStatus
        this._statusChanged = true
    }

    public trimStringField(field: OcGenericDialogSingleFieldState<SourceType>): void {
        if (typeof field.value === 'string') {
            field.setValue(field.value.trim(), this.dialogOutput)
        }
    }

    @action.bound
    public validateAllData(): void {
        this.changedFields.forEach(field => {
            this.trimStringField(field)

            if (this._createMode) {
                const result = field.validateValue(
                    field.isChanged ? field.value : field.defaultValue,
                    this.dialogOutput
                )
                field.setValidationResult(result)
            } else if (this._bulkEdit) {
                if (field.isChanged) {
                    const result = field.validateValue(field.value, this.dialogOutput)
                    field.setValidationResult(result)
                }
            } else {
                const result = field.validateValue(
                    field.isChanged ? field.value : field.initialValue,
                    this.dialogOutput
                )
                field.setValidationResult(result)
            }
        })
    }

    @action.bound
    public validateChangedData(): void {
        this.changedFields
            .filter(field => field.isChanged)
            .forEach(field => {
                this.trimStringField(field)
                const result = field.validateValue(field.value, this.dialogOutput)

                field.setValidationResult(result)
            })
    }

    public getFieldInfo(
        group: string,
        field: string
    ): {isChanged: boolean; changedValue: any; errorMessage?: string; customValidationResult?: any} {
        const fieldInfo = this._changedFields.find(item => item.groupName === group && item.fieldName === field)

        return {
            isChanged: fieldInfo?.isChanged || false,
            changedValue: fieldInfo?.value,
            errorMessage: fieldInfo?.errorMessage,
            customValidationResult: fieldInfo?.customValidationResult
        }
    }

    public isFieldChanged(group: string, field: string): boolean {
        return (
            this._changedFields.find(item => item.groupName === group && item.fieldName === field)?.isChanged || false
        )
    }
}
